import { useSnackbar } from "notistack";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createAssessmentAPIEndpoint,
  EndPoints,
} from "../../../services/AssessmentServices";
import FullScreenWarning from "../components/FullScreenWarning";
import AnswerWarning from "../components/AnswerWarning";

function ExamPage() {
  const { id } = useParams();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submissionId, setSubmissionId] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [fullscreenRequired, setFullscreenRequired] = useState(false);
  const [showAnswerWarning, setShowAnswerWarning] = useState(false);
  const timerRef = useRef(null);
  const answersRef = useRef([]);
  const submissionIdRef = useRef(null);

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // ====== START EXAM ======
  const startExam = () => {
    createAssessmentAPIEndpoint(EndPoints.startAssessment(id))
      .postWithToken()
      .then((res) => {
        setSubmissionId(res.data.id);
        submissionIdRef.current = res.data.id;
        fetchQuestions(res.data.id);
        setRemainingTime(res.data.durationMinutes * 60);
      })
      .catch((error) => {
        console.error("Error starting exam:", error);
        setLoading(false);
      });
  };
  const fetchQuestions = (id) => {
    createAssessmentAPIEndpoint(EndPoints.getQuestions(id))
      .fetch()
      .then((res) => {
        setQuestions(res.data.questions || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching questions:", error);
        setLoading(false);
      });
  };
  // ====== SUBMIT ======

  const handleSubmit = (providedAnswers) => {
    const isAnswered = answers.find(
      (ans) => ans.questionId === questions[currentIndex].questionId,
    );

    if (!isAnswered) {
      setShowAnswerWarning(true);
      return;
    }

    const finalAnswers = providedAnswers || answersRef.current;
    const currentSubmissionId = submissionId || submissionIdRef.current;

    // Stop timer safely
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    createAssessmentAPIEndpoint(EndPoints.submitAnswers)
      .postWithToken({
        assessmentId: id,
        answers: finalAnswers,
      })
      .then(() => {
        enqueueSnackbar("Submitted successfully!", { variant: "success" });

        navigate(`/assessment/result/${currentSubmissionId}`);
      })
      .catch((error) => {
        console.error("Submission failed:", error);
        enqueueSnackbar("Submission failed", { variant: "error" });
      });
  };

  // ====== FULLSCREEN HANDLING ======

  const enterFullscreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen)
      elem.requestFullscreen().catch(() => {
        console.error("Failed to enter fullscreen mode.");
      });
  };

  // ====== TIMER ======
  const startTimer = () => {
    enterFullscreen();

    timerRef.current = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleSubmit(answersRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // ====== ANSWERS ======
  const handleAnswer = (questionId, answer) => {
    const updatedAnswers = [
      ...answers.filter((ans) => ans.questionId !== questionId),
      { questionId, selectedOptionIndex: answer },
    ];
    setAnswers(updatedAnswers);
    answersRef.current = updatedAnswers;
  };

  const handleNext = () => {
    const isAnswered = answers.find(
      (ans) => ans.questionId === questions[currentIndex].questionId,
    );
    if (isAnswered) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      setShowAnswerWarning(true);
    }
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const formatTime = (seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min.toString().padStart(2, "0")}:${sec
      .toString()
      .padStart(2, "0")}`;
  };
  // ====== EFFECTS ======

  useEffect(() => {
    enterFullscreen();
  }, []);

  useEffect(() => {
    setLoading(true);
    startExam();
    startTimer();
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) setFullscreenRequired(true);
      else setFullscreenRequired(false);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      clearInterval(timerRef.current);
    };
  }, []);

  const question = questions[currentIndex];

  return (
    <>
      {loading ? (
        <div className="section">Loading...</div>
      ) : questions.length === 0 ? (
        <div className="section">No questions found for this exam.</div>
      ) : fullscreenRequired ? (
        <FullScreenWarning />
      ) : showAnswerWarning ? (
        <AnswerWarning setShowAnswerWarning={setShowAnswerWarning} />
      ) : (
        <div className="exam-page section" translate="no">
          <div className="timer">
            Time Remaining:{" "}
            <strong className={remainingTime < 60 ? "red" : ""}>
              {formatTime(remainingTime)}
            </strong>
          </div>
          <div className="container">
            {questions[currentIndex] ? (
              <>
                <div className="header">
                  Question {currentIndex + 1} of {questions.length}
                </div>
                <div className="progress-bar-wrapper">
                  <div
                    className="progress-bar"
                    style={{
                      width: `${
                        ((currentIndex + 1) / questions.length) * 100
                      }%`,
                    }}
                  ></div>
                </div>

                <div className="question-container">
                  <p className="question-text">{question.text}</p>
                  <div className="options-container">
                    {question.options.map((opt, index) => {
                      const existingAnswer = answers.find(
                        (ans) => ans.questionId === question.questionId,
                      );
                      const isChecked = existingAnswer
                        ? existingAnswer.selectedOptionIndex === index
                        : false;
                      return (
                        <label
                          key={index}
                          className={`option-label ${
                            isChecked ? "selected" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name={`q-${question.questionId}`}
                            value={opt}
                            checked={isChecked}
                            onChange={() =>
                              handleAnswer(question.questionId, index)
                            }
                          />
                          {opt}
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="navigation-buttons">
                  {currentIndex > 0 ? (
                    <button onClick={handlePrev} className="prev-btn">
                      Previous
                    </button>
                  ) : (
                    <div></div>
                  )}

                  {currentIndex < questions.length - 1 ? (
                    <button onClick={handleNext} className="next-btn">
                      Next
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSubmit(answers)}
                      className="submit-btn"
                    >
                      Submit Exam
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="section">Loading question...</div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
export default ExamPage;
